<template>
  <div class="slide slide-question-scores">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title }}</h2>
      <div v-if="data.subtitle" class="slide-subtitle">{{ data.subtitle }}</div>
    </div>

    <!-- Questions List -->
    <div class="questions-container">
      <div
        v-for="(question, index) in data.questions"
        :key="index"
        class="question-row"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <div class="question-content">
          <span class="question-text">{{ question.label }}</span>
          <span class="question-type" v-if="question.type">{{ question.type }}</span>
        </div>
        <div class="score-bar-container">
          <div class="score-bar-track">
            <div
              class="score-bar-fill"
              :class="scoreColorClass(question.score)"
              :style="{ width: `${question.score}%` }"
            ></div>
          </div>
          <span class="score-value" :class="scoreColorClass(question.score)">
            {{ question.score }}%
          </span>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="score-legend">
      <div class="legend-item">
        <span class="legend-color bg-emerald"></span>
        <span class="legend-label">{{ $t('reports.quarterly.question_scores.legend_excellent') }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color bg-amber"></span>
        <span class="legend-label">{{ $t('reports.quarterly.question_scores.legend_moderate') }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-color bg-rose"></span>
        <span class="legend-label">{{ $t('reports.quarterly.question_scores.legend_attention') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionScoresSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  methods: {
    scoreColorClass(score) {
      if (score >= 70) return 'score-high';
      if (score >= 50) return 'score-medium';
      return 'score-low';
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-question-scores {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 48px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 48px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 18px;
  color: $ink-muted;
  margin-top: 8px;
}

.questions-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
}

.question-row {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 20px 24px;
  background: #fff;
  border-radius: 16px;
  box-shadow: $shadow-soft;
  animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: $shadow-medium;
  }
}

.question-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.question-text {
  font-family: $font-body;
  font-size: 18px;
  color: $ink;
  line-height: 1.4;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.question-type {
  @extend .mono-label;
  font-size: 10px;
  padding: 4px 8px;
  background: rgba($violet-500, 0.1);
  color: $violet-500;
  border-radius: 4px;
  flex-shrink: 0;
}

.score-bar-container {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 50%;
  flex-shrink: 0;
}

.score-bar-track {
  flex: 1;
  height: 8px;
  background: rgba($ink, 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.score-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);

  &.score-high { background: $emerald; }
  &.score-medium { background: $amber; }
  &.score-low { background: $rose; }
}

.score-value {
  @extend .serif-headline;
  font-size: 32px;
  min-width: 80px;
  text-align: right;

  &.score-high { color: $emerald; }
  &.score-medium { color: $amber; }
  &.score-low { color: $rose; }
}

.score-legend {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid rgba($ink, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;

  &.bg-emerald { background: $emerald; }
  &.bg-amber { background: $amber; }
  &.bg-rose { background: $rose; }
}

.legend-label {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-muted;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
